import { createAction } from '@reduxjs/toolkit';
import { notAvailable } from 'core/common/constants';
import { normalizeError } from 'core/common/errors';
import {
  ATTRS_ANALYTICS_FLOW_NAME,
  ATTRS_ANALYTICS_FUNNEL_LOCALE,
  ATTRS_ANALYTICS_FUNNEL_NAME,
  SpanName,
} from 'core/common/observability/entities';
import { AppThunk } from 'core/common/store';
import { removePrimitiveDuplicatesFromArray } from 'core/common/utils/removePrimitiveDuplicatesFromArray';
import { getEmailFlowAttributesReports, getFunnelName } from 'core/funnel/store/selectors';
import { getOfferReportTypes } from 'core/offers/utils';
import { PaymentError, PaymentOrderStatus, RecurringData } from 'core/payments/entities';
import { getScan } from 'core/scan/store';
import { getEmail, getUserLocation } from 'core/user/store/selectors';
import { PaymentUpsellError } from '../../errors';
import {
  getOrderData,
  getPaymentCardBrand,
  getPaymentCardType,
  getPaymentType,
} from '../selectors';
import { updateOrderData } from './fetchOrder';
import { updateOrderId } from './paymentForm';
import { initializeResignFormRequest } from './resignForm';

export const recurringPaymentThreeDomainSecure = createAction<RecurringData>(
  'payments/RECURRING_PAYMENT_THREE_DOMAIN_SECURE',
);

export const recurringPaymentPending = createAction('payments/RECURRING_PAYMENT/pending');
export const recurringPaymentSucceed = createAction('payments/RECURRING_PAYMENT/succeed');

export const recurringPaymentFailed = createAction<PaymentError>(
  'payments/RECURRING_PAYMENT/failed',
);

export const recurringPaymentRequest =
  (offerId: string): AppThunk =>
  async (dispatch, getState, context) => {
    const {
      analyticsService,
      loggingService: logger,
      observabilitySystem,
      errorMonitoringService,
      paymentService,
      funnelSettingsService,
    } = context;

    const userLocation = getUserLocation(getState());
    const funnelName = getFunnelName(getState());
    const email = getEmail(getState());
    const emailFlowAttributesReports = getEmailFlowAttributesReports(getState());
    const orderData = getOrderData(getState());
    const paymentType = getPaymentType(getState());
    const paymentCardBrand = getPaymentCardBrand(getState());
    const paymentCardType = getPaymentCardType(getState());
    const scan = getScan(getState());

    const reportTypes = getOfferReportTypes(emailFlowAttributesReports, offerId);

    dispatch(recurringPaymentPending());

    const spanAttributes = {
      [ATTRS_ANALYTICS_FUNNEL_NAME]: funnelName,
      [ATTRS_ANALYTICS_FLOW_NAME]: funnelSettingsService.getFlowName(),
      [ATTRS_ANALYTICS_FUNNEL_LOCALE]: funnelSettingsService.getFunnelLocale(),
    };

    const span = observabilitySystem.startSpan(SpanName.RECURRING_PAYMENT_REQUEST);
    span.setAttributes(spanAttributes);

    try {
      const activeUpsellPayments = await paymentService.getPayments({ offerId, email });

      if (!!activeUpsellPayments.length) {
        throw new PaymentUpsellError();
      }

      const reports = Array.isArray(orderData.reports) ? orderData.reports : [];
      const mergedReports = removePrimitiveDuplicatesFromArray([...reports, ...reportTypes]);

      const { orderId } = await paymentService.createOrder({
        email,
        offer_id: offerId,
        extra_data: {
          ...orderData,
          reports: mergedReports,
          scan: {
            photoId: scan.photoId,
          },
        },
      });

      const result = await paymentService.createRecurringPayment({
        email,
        offerId,
        orderId,
        paymentType,
      });

      if (result.status === PaymentOrderStatus.THREE_DS_VERIFY) {
        dispatch(updateOrderData({ ...orderData, reports: mergedReports }));
        dispatch(
          recurringPaymentThreeDomainSecure({
            ...result.recurringData,
            orderId: result.order_id,
            status: result.status,
          }),
        );
        span.succeed();
        return;
      }

      if (
        result.status === PaymentOrderStatus.AUTH_FAILED ||
        result.status === PaymentOrderStatus.DECLINED
      ) {
        analyticsService.paymentError({
          reason: result.error.message || result.error.code || '',
          productId: offerId,
          paymentMethod: paymentType ?? '',
        });

        if (!result.recurringData.tokenId) {
          await dispatch(createOrderByOfferIdRequest(offerId));
          dispatch(recurringPaymentFailed(result.error));
          span.succeed();
          return;
        }

        dispatch(updateOrderData({ ...orderData, reports: mergedReports }));
        dispatch(
          initializeResignFormRequest({
            offerId,
            tokenId: result.recurringData.tokenId,
            paymentId: result.order_id,
          }),
        );
        span.succeed();
        return;
      }

      dispatch(updateOrderData({ ...orderData, reports: mergedReports }));

      analyticsService.purchaseSucceed({
        productId: offerId,
        paymentMethod: paymentType ?? '',
        paymentCardBrand,
        paymentCardType,
        orderId,
      });
      dispatch(recurringPaymentSucceed());

      span.succeed();
    } catch (err) {
      const error = normalizeError(err);

      span.fail(error);

      logger.error(error);

      dispatch(createOrderByOfferIdRequest(offerId));
      dispatch(recurringPaymentFailed(error));
      analyticsService.paymentError({
        reason: error.message,
        productId: offerId,
        paymentMethod: paymentType ?? '',
      });

      errorMonitoringService.captureError(error, {
        tags: {
          offer_id: offerId,
          get_params: location.search || notAvailable,
          error_message: error.message,
          error_reason: 'recurring_payment_failed',
          funnel_name: funnelName,
          ...userLocation,
        },
      });
    }
  };

export const createOrderByOfferIdRequest =
  (offerId: string): AppThunk =>
  async (dispatch, getState, context) => {
    const { paymentService } = context;

    const email = getEmail(getState());
    const orderData = getOrderData(getState());
    const emailFlowAttributesReports = getEmailFlowAttributesReports(getState());
    const scan = getScan(getState());

    const reportTypes = getOfferReportTypes(emailFlowAttributesReports, offerId);
    const reports = Array.isArray(orderData.reports) ? orderData.reports : [];
    const mergedReports = removePrimitiveDuplicatesFromArray([...reports, ...reportTypes]);

    const { orderId } = await paymentService.createOrder({
      email,
      offer_id: offerId,
      extra_data: {
        ...orderData,
        reports: mergedReports,
        scan: {
          photoId: scan.photoId,
        },
      },
    });

    dispatch(updateOrderId(orderId));
  };
